// Iter47-A — Admin → Incident Categories (master data CRUD, 11 system rows §3.1).
import React, { useState } from "react";
import { Tag } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import { useT } from "../lib/i18n";
import {
  useIncidentCategories,
  useCreateIncidentCategory,
  useUpdateIncidentCategory,
  useDeleteIncidentCategory,
  useTenantTypes,
} from "../api/sitroom";
import {
  mono, inputStyle, Label, NameRow, SystemBadge, RowActions, NewRowButton, SaveCancelButtons, pickName,
} from "../components/MasterDataAtoms";

const SEVERITIES = ["s1", "s2", "s3", "s4"];

export default function AdminIncidentCategories() {
  const { user } = useAuth();
  const { locale } = useT();
  const addToast = useStore((s) => s.addToast);
  const { data: rows = [], isLoading } = useIncidentCategories();
  const { data: tenantTypes = [] } = useTenantTypes();
  const createMut = useCreateIncidentCategory();
  const updateMut = useUpdateIncidentCategory();
  const deleteMut = useDeleteIncidentCategory();
  const isPlatform = user?.role === "platform_admin";

  const [editing, setEditing] = useState(null);

  const onSave = async (key, body, isNew) => {
    try {
      if (isNew) {
        await createMut.mutateAsync(body);
        addToast({ type: "success", title: "Category created", body: body.names?.en });
      } else {
        await updateMut.mutateAsync({ key, body });
        addToast({ type: "success", title: "Category updated", body: body.names?.en || key });
      }
      setEditing(null);
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete incident category "${pickName(row, locale)}"?`)) return;
    try {
      await deleteMut.mutateAsync(row.key);
      addToast({ type: "success", title: "Category deleted" });
    } catch (e) {
      addToast({ type: "error", title: "Delete failed", body: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="admin-incident-categories">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">
          Incident Categories · {rows.length}
        </div>
        {isPlatform && <NewRowButton onClick={() => setEditing("new")} label="New incident category" testId="incident-category-new-btn" />}
      </div>

      {isLoading && <div style={mono(11, "var(--dimmer, #9198b0)")}>Loading…</div>}

      <div className="space-y-2">
        {rows.map((row) => (
          <Row key={row.key} row={row} isPlatform={isPlatform} locale={locale} tenantTypes={tenantTypes}
            editing={editing === row.key}
            onEdit={() => setEditing(row.key)}
            onCancel={() => setEditing(null)}
            onSave={(body) => onSave(row.key, body, false)}
            onDelete={() => onDelete(row)} />
        ))}
        {editing === "new" && (
          <Row row={null} isPlatform={isPlatform} locale={locale} tenantTypes={tenantTypes}
            editing={true}
            onCancel={() => setEditing(null)}
            onSave={(body) => onSave(null, body, true)} />
        )}
      </div>
    </div>
  );
}

const Row = ({ row, isPlatform, locale, tenantTypes, editing, onEdit, onCancel, onSave, onDelete }) => {
  const [key, setKey] = useState(row?.key || "");
  const [names, setNames] = useState(row?.names || { en: "", de: "", hu: "" });
  const [severity, setSeverity] = useState(row?.default_severity || "s3");
  const [originating, setOriginating] = useState(row?.originating_tenant_types || []);
  const [autoLinked, setAutoLinked] = useState(row?.auto_linked_party_types || []);

  const toggle = (arr, val, setter) => setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const handleSave = () => {
    if (!names.en?.trim()) return;
    const body = {
      names: { en: names.en?.trim(), de: names.de?.trim() || names.en?.trim(), hu: names.hu?.trim() || names.en?.trim() },
      default_severity: severity,
      originating_tenant_types: originating,
      auto_linked_party_types: autoLinked,
    };
    if (!row) body.key = key.trim();
    onSave(body);
  };

  if (editing) {
    return (
      <div className="rounded-md p-4"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(24,95,165,0.5)" }}
        data-testid={row ? `incident-category-edit-${row.key}` : "incident-category-edit-new"}>
        {!row && (
          <div className="mb-3">
            <Label>Key (lowercase, alpha-numeric)</Label>
            <input value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
              placeholder="e.g. supplier_audit" style={inputStyle} data-testid="incident-category-edit-key" />
          </div>
        )}
        <NameRow names={names} onChange={setNames} testIdPrefix="incident-category-edit" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
          <div>
            <Label>Default severity</Label>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={inputStyle} data-testid="incident-category-edit-severity">
              {SEVERITIES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-3">
          <Label>Can be opened by these tenant types</Label>
          <div className="flex flex-wrap gap-1">
            {tenantTypes.map((tt) => {
              const on = originating.includes(tt.key);
              return (
                <button key={tt.key} type="button" onClick={() => toggle(originating, tt.key, setOriginating)}
                  className="px-2 py-0.5 rounded"
                  style={{
                    background: on ? "rgba(24,95,165,0.18)" : "transparent",
                    border: `1px solid ${on ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
                    color: on ? "#9ec3ec" : "var(--text-dim, #c5cad8)",
                    ...mono(9, undefined, 600, "0.3px"), cursor: "pointer",
                  }}
                  data-testid={`incident-category-edit-orig-${tt.key}`}
                  aria-pressed={on}>{pickName(tt, locale)}</button>
              );
            })}
          </div>
        </div>
        <div className="mb-3">
          <Label>Default auto-linked party types</Label>
          <div className="flex flex-wrap gap-1">
            {tenantTypes.map((tt) => {
              const on = autoLinked.includes(tt.key);
              return (
                <button key={tt.key} type="button" onClick={() => toggle(autoLinked, tt.key, setAutoLinked)}
                  className="px-2 py-0.5 rounded"
                  style={{
                    background: on ? "rgba(243,177,74,0.15)" : "transparent",
                    border: `1px solid ${on ? "rgba(243,177,74,0.50)" : "rgba(255,255,255,0.10)"}`,
                    color: on ? "#f3b14a" : "var(--text-dim, #c5cad8)",
                    ...mono(9, undefined, 600, "0.3px"), cursor: "pointer",
                  }}
                  data-testid={`incident-category-edit-link-${tt.key}`}
                  aria-pressed={on}>{pickName(tt, locale)}</button>
              );
            })}
          </div>
        </div>
        <SaveCancelButtons onSave={handleSave} onCancel={onCancel} testIdPrefix="incident-category-edit" />
      </div>
    );
  }

  return (
    <div className="rounded-md p-3"
      style={{ background: "var(--bg-surface, #0d0f18)", border: `1px solid ${row.system ? "rgba(243,177,74,0.25)" : "rgba(255,255,255,0.10)"}` }}
      data-testid={`incident-category-row-${row.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Tag size={13} color="#9ec3ec" />
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
            {pickName(row, locale)}
          </span>
          <span className="px-1.5 py-0.5 rounded" style={{ background: "rgba(24,95,165,0.18)", color: "#9ec3ec", ...mono(9, undefined, 700, "0.4px") }}>
            {row.default_severity?.toUpperCase()}
          </span>
          <span style={mono(10, "var(--dimmer, #9198b0)")}>key={row.key}</span>
          {row.system && <SystemBadge />}
        </div>
        {isPlatform && (
          <RowActions row={row} onEdit={onEdit} onDelete={onDelete} canDelete={!row.system} testIdPrefix="incident-category" />
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-2" style={mono(10, "var(--text-dim, #c5cad8)")}>
        {(row.originating_tenant_types || []).length > 0 && (
          <span>Open by: <b style={{ color: "#9ec3ec" }}>{(row.originating_tenant_types || []).join(", ")}</b></span>
        )}
        {(row.auto_linked_party_types || []).length > 0 && (
          <span>Auto-link: <b style={{ color: "#f3b14a" }}>{(row.auto_linked_party_types || []).join(", ")}</b></span>
        )}
      </div>
    </div>
  );
};
